/**
 * Debug Settings
 *
 * Central configuration for controlling debug logs across the application.
 * These settings apply consistently across all environments (dev, staging, production).
 *
 * To enable specific logs, set the relevant flag to true.
 */
import type { Store } from "redux";
import { AnyAction } from "redux";

// Get a debug setting from localStorage
function getDebugSetting(key: string, defaultValue: boolean): boolean {
  if (typeof localStorage === "undefined") return defaultValue;

  try {
    const value = localStorage.getItem(`DEBUG_${key}`);
    return value === "true";
  } catch (e) {
    return defaultValue;
  }
}

export const DEBUG = {
  // Component render counts
  COMPONENT_RENDERS: getDebugSetting("COMPONENT_RENDERS", false),

  // Redux state changes
  REDUX_CHANGES: getDebugSetting("REDUX_CHANGES", false),

  // Function reference changes
  FUNCTION_CHANGES: false,

  // Props and state changes
  PROPS_CHANGES: getDebugSetting("PROPS_CHANGES", false),

  // ListView specific logs
  LISTVIEW_RENDERS: false,

  // ListView playlist loading and updating logs
  LISTVIEW_PLAYLIST_LOGS: getDebugSetting("LISTVIEW_PLAYLIST_LOGS", false),

  // PlaylistContainer loading and initialization logs
  PLAYLIST_CONTAINER_LOGS: getDebugSetting("PLAYLIST_CONTAINER_LOGS", false),

  // Player state transitions and high-level events
  PLAYER_STATE: false,

  // Player implementation details (play/pause/load calls)
  PLAYER_IMPLEMENTATION: false,

  // Media session integration details
  MEDIA_SESSION: false,

  // Safari player diagnostic logs (detailed position tracking logs)
  SAFARI_PLAYER_LOGS: getDebugSetting("SAFARI_PLAYER_LOGS", false),

  // Log detailed timing information for position updates in the music player
  POSITION_UPDATE_TIMING: getDebugSetting("POSITION_UPDATE_TIMING", false),

  // Log Redux state size information
  REDUX_STATE_SIZE: getDebugSetting("REDUX_STATE_SIZE", false),

  // Log messages related to first play initialization
  FIRST_PLAY_MESSAGES: getDebugSetting("FIRST_PLAY_MESSAGES", false),

  // Log warning messages about CloudFront CORS issues
  CLOUDFRONT_WARNINGS: getDebugSetting("CLOUDFRONT_WARNINGS", false),

  // Display current Redux state size in console
  showReduxStateSize: async () => {
    try {
      // Dynamically import the monitor to avoid SSR issues
      const { getReduxSizeMonitoringStatus } = await import(
        "./reduxSizeMonitor"
      );
      const status = getReduxSizeMonitoringStatus();

      console.log("Redux State Size Monitoring Status:");
      console.log(`- Enabled: ${status.enabled}`);
      console.log(`- Baseline established: ${status.baselineEstablished}`);

      if (status.baselineEstablished) {
        console.log(`- Baseline age: ${status.baselineAge.toFixed(1)} days`);
        console.log(
          `- Current state size: ${(
            status.currentSizes.total /
            (1024 * 1024)
          ).toFixed(2)} MB`
        );
        console.log(
          `- Percent of max expected: ${status.currentSizes.percentOfMax.toFixed(
            1
          )}%`
        );
        console.log(
          `- Measurements recorded: ${status.currentSizes.measurements}`
        );
      }
    } catch (e) {
      console.error("Failed to get Redux state size status", e);
    }
  },

  // Control for monitoring systems with changes effective after relaunch
  toggleMonitoring: async (
    system: "performance" | "redux",
    enable: boolean
  ) => {
    try {
      const monitoringConfig = await import("./monitoringConfig");

      if (system === "performance") {
        const success = monitoringConfig.setPerformanceMonitoring(enable);
        if (success) {
          console.log(
            `Performance monitoring will be ${
              enable ? "ENABLED" : "DISABLED"
            } after page refresh.`
          );
        } else {
          console.error("Failed to set performance monitoring state.");
        }
      } else if (system === "redux") {
        const success = monitoringConfig.setReduxSizeMonitoring(enable);
        if (success) {
          console.log(
            `Redux size monitoring will be ${
              enable ? "ENABLED" : "DISABLED"
            } after page refresh.`
          );
        } else {
          console.error("Failed to set Redux size monitoring state.");
        }
      }

      console.log("Please refresh the page for changes to take effect.");
    } catch (e) {
      console.error("Failed to toggle monitoring:", e);
    }
  },

  // Show monitoring status
  showMonitoringStatus: async () => {
    try {
      const { getMonitoringStatus } = await import("./monitoringConfig");
      const status = getMonitoringStatus();

      console.log("Monitoring Systems Status:");
      console.log(
        `- Performance Monitoring: ${
          status.performanceMonitoring ? "ENABLED" : "DISABLED"
        }`
      );
      console.log(
        `- Redux Size Monitoring: ${
          status.reduxSizeMonitoring ? "ENABLED" : "DISABLED"
        }`
      );
      console.log(
        "Note: Changes to these settings only take effect after page refresh."
      );
    } catch (e) {
      console.error("Failed to get monitoring status:", e);
    }
  },

  // Reset the Redux state size monitoring
  resetReduxSizeMonitoring: async () => {
    try {
      // Dynamically import the monitor to avoid SSR issues
      const { resetReduxSizeMonitoring } = await import("./reduxSizeMonitor");

      // We can't directly import the store since it's created by a function
      // Instead, add a helper function to the window in development
      if (typeof window !== "undefined") {
        // Access the Redux store through the devtools extension
        // This only works if Redux DevTools extension is installed, but that's
        // a reasonable assumption for anyone using this debug function
        const reduxDevTools = (window as any).__REDUX_DEVTOOLS_EXTENSION__;

        if (reduxDevTools) {
          const state = reduxDevTools.getState();
          if (state) {
            console.log("Found Redux store via DevTools, resetting monitoring");
            // Create a simple object with the required methods and cast it to Store
            const mockStore = {
              getState: () => state,
              dispatch: () => ({ type: "@@RESET_MONITOR/NOOP" }),
              subscribe: () => {
                return () => {};
              },
              replaceReducer: () => mockStore,
            } as unknown as Store;

            resetReduxSizeMonitoring(mockStore);
            console.log("Redux state size monitoring has been reset");
            return;
          }
        }

        console.error(
          "Redux DevTools not found. Please install Redux DevTools extension or run this from a component with access to the store."
        );
      } else {
        console.error(
          "Cannot reset Redux state size monitoring in server environment"
        );
      }
    } catch (e) {
      console.error("Failed to reset Redux state size monitoring", e);
    }
  },

  // Scroll behavior
  SCROLL_BEHAVIOR: false,
};

export default DEBUG;
